import config from '@config/config';
import HttpService from '../http';
import LocalStorageService from '../localStorage';
import { isPublicGuestPath } from '@utils/helpers';

// A page a guest with no/invalid session must still be able to view (e.g. a
// /survey/:id?guestUserClicked=true link) shouldn't be yanked out from under
// them just because some stale token in localStorage fails to refresh —
// useRouterPaths already keeps the viewer on these pages for the same
// reason; this mirrors it so a dead refresh token doesn't undo that via a
// hard redirect.
const shouldHardRedirectToRoot = () =>
  !isPublicGuestPath(window.location.pathname);

class ApiService {
  constructor(baseUrl) {
    this.http = new HttpService(baseUrl);
    this.internalRefreshToken = this.internalRefreshToken.bind(this);
    this.refreshTokenPromise = null;

    this.http.addRequestInterceptor((axiosConfig) => {
      const accessToken = LocalStorageService.getAccessToken();
      if (!accessToken) {
        return axiosConfig;
      }

      axiosConfig.headers = axiosConfig.headers || {}; // eslint-disable-line no-param-reassign
      axiosConfig.headers.Authorization = `Bearer ${accessToken}`; // eslint-disable-line no-param-reassign
      return axiosConfig;
    });

    this.http.addResponseInterceptor((response) => response, (error) => {
      const status = error.response ? error.response.status : null;

      if (error.config.url.includes('/session/current')) {
        return Promise.reject(error);
      }

      if (error.config.url.includes('/refreshToken')) {
        if (shouldHardRedirectToRoot()) {
          window.location.href = '/';
        }
        return Promise.reject(error);
      }

      // Several requests can 401 at once on page load. Without the
      // `hasRetried` guard, a request retried with a freshly refreshed
      // token that still comes back 401 would re-enter this branch and
      // refresh again, forever. One retry per request is as far as this
      // goes — if that still fails, treat the session as unrecoverable.
      if (status === 401 && !error.config.hasRetried) {
        error.config.hasRetried = true; // eslint-disable-line no-param-reassign
        return this.getRefreshedAccessToken().then((accessToken) => {
          if (!accessToken) {
            if (shouldHardRedirectToRoot()) {
              window.location.href = '/';
            }
            return Promise.reject(error);
          }

          error.config.headers.Authorization = `Bearer ${accessToken}`; // eslint-disable-line no-param-reassign
          return this.http.axios.request(error.config);
        });
      }

      if (status === 401 && shouldHardRedirectToRoot()) {
        window.location.href = '/';
      }

      return Promise.reject(error);
    });
  }

  // Concurrent 401s would otherwise each call internalRefreshToken
  // independently, racing each other against a backend that rotates
  // refresh tokens on every use — whichever call loses the race gets back
  // a refresh token that's already been invalidated by a sibling call,
  // which is what produced the runaway 401 -> refreshToken -> retry -> 401
  // loop this fixes. Sharing one in-flight promise means every concurrent
  // 401 waits on the same single refresh instead of starting its own.
  getRefreshedAccessToken() {
    if (!this.refreshTokenPromise) {
      this.refreshTokenPromise = this.internalRefreshToken().finally(() => {
        this.refreshTokenPromise = null;
      });
    }
    return this.refreshTokenPromise;
  }

  async internalRefreshToken() {
    const refreshToken = LocalStorageService.getRefreshToken();
    if (!refreshToken) {
      LocalStorageService.removeAccessToken();
      LocalStorageService.removeRefreshToken();
      LocalStorageService.removeAppCache();
      return null;
    }

    try {
      const response = await this.http.post(`${config.authApiUrl}/refreshToken`, {refreshToken});
      const tokens = response.data;

      LocalStorageService.setAccessToken(tokens.accessToken);
      LocalStorageService.setRefreshToken(tokens.refreshToken);

      return tokens.accessToken;
    } catch (err) {
      LocalStorageService.removeAccessToken();
      LocalStorageService.removeRefreshToken();
      LocalStorageService.removeAppCache();
      return null;
    }
  }
}

export default ApiService;
